import React, { useState, useRef, useEffect } from "react";
import { saveAs } from "file-saver";
import {
  Box, Flex, Text, Button, VStack,
  Heading, Icon, Input, Badge, SimpleGrid, Center,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { FiDownload, FiCalendar, FiFileText } from "react-icons/fi";
import { TbReportAnalytics } from "react-icons/tb";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseDatePayload = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return { date: d, month: m, year: y };
};

// Column order matches the API response shape exactly
const SOVI_HEADERS = [
  "Year",
  "Month",
  "Region",
  "Sub-Region",
  "Town Code",
  "Shop No",
  "Customer",
  "Outlet Type",
  "Q4f. Outlet Class",
  "Outlet Type-Class",
  "Town Name",
  "Outlet Name",
  "Store Address",
  "RSM Name",
  "ASM Name",
  "SO SE Name",
  "PS DS Name",
  "Cooler Score",
  "Purity Score",
  "Cooler Bonus",
  "Cooler puritypercent",
  "Cooler impuritypercent",
  "SOVI Percentage",
  "SOVI Score",
  "CHILLED KO FACINGS",
  "CHILLED NARTD FACINGS",
  "TOTAL CHILLED FACINGS",
  "WARM KO FACINGS",
  "WARM NARTD FACINGS",
  "TOTAL WARM FACINGS",
  "TOTAL KO FACINGS",
  "TOTAL NARTD FACINGS",
  "TOTAL FACINGS",
  "AVAILABILITY",
  "Q9b. RGB Space Occupied by KO Product-Shelf 1",
  "Q9b. RGB Space Occupied by KO Product-Shelf 2",
  "Q9b. RGB Space Occupied by KO Product-Shelf 3",
  "Q9b. RGB Space Occupied by KO Product-Shelf 4",
  "Q9b. RGB Space Occupied by KO Product-Shelf 5",
  "Q9b. RGB Space Occupied by KO Product-Shelf 6",
  "Q9b. RGB Space Occupied by KO Product-Shelf 7",
  "Q9b. RGB Space Occupied by KO Product-Shelf 8",
  "Q9b. RGB Space Occupied by KO Product-Shelf 9",
  "Q9b. RGB Space Occupied by KO Product-Shelf 10",
  "Q10.1 Coke 250 ml RGB",
  "Q10.2 Sprite 250 ml RGB",
  "Q10.3 Fanta 250 ml RGB",
  "Q10.5 Kinley Soda 250 ml RGB",
  "Q10.9 Coke 250 ml PET",
  "Q10.10 Sprite 250 ml PET",
  "Q10.11 Fanta Orange 250 ml PET",
  "Q10.12 Fanta Lemon 250 ml PET",
  "Q12a.1 Coke ASSP 500ml",
  "Q12a.2 Sprite ASSP 500ml",
  "Q12a.3 Fanta Orange ASSP 500ml",
  "Q12a.7 Kinley Water 500/600/750ml",
  "Q13.1 Coke 1000 ML",
  "Q13.2 Sprite 1000 ML",
  "Q13.3 Fanta 1000 ML",
  "Q13.4 Kinley Water 1000 ML",
  "Q14.1 Coke FC1500",
  "Q14.2 Sprite FC1500",
  "Q14.3 Fanta FC1500",
  "Q15.1 Coke FC2000",
  "Q15.2 Sprite FC2000",
  "Q15.3 Fanta Orange FC2000",
  "Q15.4 Fanta Lemon FC2000",
  "Q16.1 Coke FC2250",
  "Q16.2 Sprite FC2250",
  "Q16.3 Fanta Orange FC2250",
  "Q16.4 Fanta Lemon FC2250",
  "ACTIVATION",
  "NoOfDisplays",
  "Total Score",
];

// ─── Exported export handler (usable from DownloadExcel.js too) ───────────────

export const handleSoviExport = async (from, to, setLoading) => {
  setLoading(true);
  const body = { from: parseDatePayload(from), to: parseDatePayload(to) };

  try {
    const res = await apiClient.post(
      `${API_CONFIG.ENDPOINTS.REPORT_CENTER.SOVIREPORT}`,
      body
    );
    if (res.status !== 200) throw new Error("API failed: " + res.status);

    const list = Array.isArray(res.data) ? res.data : [res.data];

    const headerStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const dataStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      font: { color: { rgb: "2D3748" }, sz: 11 },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const wsData = [
      SOVI_HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...list.map((row) =>
        SOVI_HEADERS.map((h) => ({
          v: row[h] !== undefined && row[h] !== null ? String(row[h]) : "",
          t: "s",
          s: dataStyle,
        }))
      ),
    ];

    const XLSX = await import("xlsx-js-style");
    const ws    = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = SOVI_HEADERS.map(() => ({ wch: 22 }));
    ws["!rows"] = [{ hpt: 30 }, ...list.map(() => ({ hpt: 22 }))];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SOVI Report");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `SOVI_Report_${from}_to_${to}.xlsx`
    );
  } catch (err) {
    console.error("SOVI export error:", err);
    alert("Failed to generate SOVI report: " + err.message);
  } finally {
    setLoading(false);
  }
};

// ─── Standalone component ─────────────────────────────────────────────────────

export default function SoviReport() {
  const [loading, setLoading] = useState(false);

  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day   = String(now.getDate()).padStart(2, "0");

  const [fromDate, setFromDate] = useState(`${year}-${month}-01`);
  const [toDate,   setToDate]   = useState(`${year}-${month}-${day}`);

  const fromDateRef = useRef(fromDate);
  const toDateRef   = useRef(toDate);
  useEffect(() => { fromDateRef.current = fromDate; }, [fromDate]);
  useEffect(() => { toDateRef.current   = toDate;   }, [toDate]);

  const handleExport = () =>
    handleSoviExport(fromDateRef.current, toDateRef.current, setLoading);

  // Columns grouped for the info badges
  const columnGroups = [
    { label: "Store Info",      color: "blue",   count: 17 },
    { label: "Cooler & Purity", color: "orange", count: 5  },
    { label: "SOVI Metrics",    color: "purple", count: 9  },
    { label: "Shelf RGB",       color: "green",  count: 10 },
    { label: "SKU Availability",color: "teal",   count: 24 },
    { label: "Scores",          color: "red",    count: 3  },
  ];

  return (
    <Box bg="white" borderRadius="2xl" p={6} boxShadow="xl" border="1px solid" borderColor="gray.200">
      {/* Header */}
      <Flex align="center" gap={3} mb={2}>
        <Icon as={TbReportAnalytics} boxSize={7} color="red.600" />
        <Heading size="md" color="gray.800">SOVI Report</Heading>
        <Badge colorScheme="purple" variant="subtle" fontSize="xs" px={3} py={1} borderRadius="full">
          {SOVI_HEADERS.length} columns
        </Badge>
      </Flex>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Download store-level SOVI (Share of Visual Inventory) data — cooler scores, purity,
        facings breakdown, SKU availability, and total scores — for the selected date range.
      </Text>

      {/* Column group badges */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={6}>
        {columnGroups.map((g) => (
          <Flex
            key={g.label}
            align="center"
            gap={2}
            bg="gray.50"
            px={3}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
          >
            <Box w="8px" h="8px" borderRadius="full" bg={`${g.color}.500`} flexShrink={0} />
            <Box minW={0}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.700" noOfLines={1}>{g.label}</Text>
              <Text fontSize="xs" color="gray.400">{g.count} cols</Text>
            </Box>
          </Flex>
        ))}
      </SimpleGrid>

      {/* Date pickers + Download */}
      <VStack spacing={5} align="stretch">
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          {/* From Date */}
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">From Date</Text>
            <Box position="relative">
              <Icon
                as={FiCalendar}
                position="absolute" left="4" top="50%"
                transform="translateY(-50%)"
                color="red.500" zIndex={1}
              />
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(197,32,39,0.15)" }}
              />
            </Box>
          </Box>

          {/* To Date */}
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">To Date</Text>
            <Box position="relative">
              <Icon
                as={FiCalendar}
                position="absolute" left="4" top="50%"
                transform="translateY(-50%)"
                color="red.500" zIndex={1}
              />
              <Input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(197,32,39,0.15)" }}
              />
            </Box>
          </Box>
        </Flex>

        {/* Download button */}
        <Button
          onClick={handleExport}
          isLoading={loading}
          loadingText="Generating SOVI Report..."
          size="lg"
          bg="red.600"
          color="white"
          leftIcon={<FiDownload />}
          borderRadius="xl"
          fontWeight="bold"
          fontSize="md"
          _hover={{ bg: "red.700", transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(197,32,39,0.35)" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s"
          w="100%"
        >
          Download SOVI Report
        </Button>

        {/* Helper note */}
        <Center>
          <Flex align="center" gap={2} color="gray.400">
            <Icon as={FiFileText} boxSize={4} />
            <Text fontSize="xs">
              Exports as <strong>.xlsx</strong> — file name includes the selected date range
            </Text>
          </Flex>
        </Center>
      </VStack>
    </Box>
  );
}
